import { FiChevronRight } from 'react-icons/fi';
import DischargeTimerRow from './DischargeTimerRow';

/**
 * Mobile exit/emergency asset-group card with 90-minute timer states.
 *
 * Public interface: group identity + progress + timer `status`
 * (`idle` | `running` | `complete`). Hides the header/progress chrome and the
 * start / countdown / complete timer row.
 *
 * Props:
 *   groupCode — primary title (e.g. "BOOG")
 *   unitCount — used in subtitle (e.g. 4 → "4 units · …")
 *   typeLabel — subtitle suffix (default "exit & emergency")
 *   testedCount / totalCount — progress pill
 *   status — `'idle' | 'running' | 'complete'`
 *   remainingMs — countdown when running (displayed as mm:ss)
 *   expanded — chevron rotation
 *   onToggle — expand/collapse header click
 *   onStart — start timer (idle)
 *   onRequestReset — optional reset when running/complete
 *   className — extra class(es)
 */
export default function MobileAssetGroupEmerg({
  groupCode,
  unitCount = 0,
  typeLabel = 'exit & emergency',
  testedCount = 0,
  totalCount = 0,
  status = 'idle',
  remainingMs = 0,
  expanded = false,
  onToggle,
  onStart,
  onRequestReset,
  className = '',
  ...props
}) {
  const timerState = status === 'running' || status === 'complete' ? status : 'idle';
  const progress = `${testedCount} / ${totalCount}`;
  const pillState = totalCount > 0 && testedCount === totalCount
    ? 'complete'
    : testedCount > 0
      ? 'partial'
      : 'empty';
  const subtitle = `${unitCount} ${unitCount === 1 ? 'unit' : 'units'} · ${typeLabel}`;

  return (
    <article
      className={[
        'mobile-asset-group',
        'mobile-asset-group--emerg',
        expanded ? 'mobile-asset-group--expanded' : '',
        className,
      ].filter(Boolean).join(' ')}
      data-timer={timerState}
      {...props}
    >
      <button
        type="button"
        className="mobile-asset-group-emerg__header"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span className="mobile-asset-group__lead">
          <FiChevronRight
            size={18}
            className="mobile-asset-group__chevron"
            aria-hidden
          />
          <span className="mobile-asset-group__identity">
            <span className="mobile-asset-group__code">{groupCode}</span>
            <span className="mobile-asset-group__type">{subtitle}</span>
          </span>
        </span>
        <span className={`mobile-asset-group__progress mobile-asset-group__progress--${pillState}`}>
          {progress}
        </span>
      </button>

      <DischargeTimerRow
        groupCode={groupCode}
        status={timerState}
        remainingMs={remainingMs}
        onStart={onStart}
        onRequestReset={onRequestReset}
      />
    </article>
  );
}
